import React, { useCallback, useState } from "react";
import styled from "styled-components";
import LoadingIndicator from "../common/ui/LoadingIndicator/LoadingIndicator";
import Collapsible from "components/common/ui/Collapsible/Collapsible";
import styles from "styles/styles";
import useTranslate from "hooks/common/useTranslate/useTranslate";
import { formatDate, formatTimeHour } from "services/common/dates/dates";
import { Event } from "interfaces/content";
import useLocale from "hooks/common/useLocale/useLocale";
import { isEmpty } from "lodash";
import Button from "components/common/ui/buttons/Button/Button";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Locale } from "interfaces/urls";
import { hasPassed } from "utils/dateAndTime";
import AddToCalendarLink, {
    isAddableToCalendar,
    isJoinable,
} from "components/common/AddToCalendarLink/AddToCalendarLink";

interface Props {
    title: string;
    showAllLabel: string;
    showAllUrl: string;
    isLoading: boolean;
    events: Event[];
}

const Container = styled.div`
    margin-top: 24px;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
    position: relative;
    display: flex;
    justify-content: center;
`;

const Header = styled.h5`
    margin: 0;
`;

const ListItem = styled.div`
    margin: ${styles.spacing[4]} 0;
    border: 1px solid ${styles.colors.grey4};
    width: 100%;
    align-items: center;
    background-color: ${styles.colors.lightGrey};

    :hover {
        border: 1px dotted ${styles.colors.red};
    }
`;

const ListItemDescription = styled.h6`
    color: ${styles.colors.grey7};
    margin: 0;
    text-align: left;
`;

const ListItemDetails = styled.p`
    color: ${styles.colors.red};
    margin: 0 0 ${styles.spacing[1]} 0;
    text-align: left;
    font-size: ${styles.fontSize.small};
`;

const ExcerptContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const EventContent = styled.div`
    padding: ${styles.spacing[4]};
`;

const ExpandedEventBlock = styled.div`
    padding: 0px 20px 10px 45px;
    white-space: pre-wrap;

    p {
        margin: 0;
    }
`;

const ShowMoreOrLessLink = styled.a`
    text-decoration: none;
    font-size: ${styles.fontSize.small};
`;

const NoResults = styled.div`
    font-style: italic;
    font-size: ${styles.fontSize.small};
    padding-bottom: ${styles.spacing[4]};
`;

const isNonExpandable = (event: Event, locale: Locale) =>
    isEmpty(event.details?.[locale]?.trim()) &&
    isEmpty(event.recordingUrl?.trim()) &&
    isEmpty(event.documents);

const UpcomingEventsList: React.FC<Props> = ({
    title,
    events,
    isLoading,
    showAllLabel,
    showAllUrl,
}) => {
    const translate = useTranslate();
    const locale = useLocale();

    // Proper filtering of events that have end date set.
    const displayableEvents = events.filter(
        (event) => !event.endDate || !hasPassed(event.endDate, 15)
    );

    const [collapsed, setCollapsed] = useState<boolean[]>(displayableEvents.map(() => true));

    const dispatch = useDispatch();

    const handleButtonClick = useCallback(() => {
        window.scrollTo(0, 0);
        dispatch(push(showAllUrl));
    }, [dispatch, showAllUrl]);

    return (
        <>
            {isLoading && (
                <Container>
                    <StyledLoadingIndicator isLoading={isLoading} />
                </Container>
            )}
            <Container>
                <Header>{title}</Header>
                {displayableEvents.length === 0 && (
                    <NoResults>{translate("frontPage.noUpcomingEvents")}</NoResults>
                )}
                {displayableEvents.map((event, idx) => (
                    <ListItem
                        key={idx}
                        onClick={(e) => {
                            e.preventDefault();

                            if (isNonExpandable(event, locale)) {
                                return;
                            }

                            setCollapsed(
                                collapsed.map((value, index) => (index === idx ? !value : value))
                            );
                        }}
                    >
                        <EventContent>
                            <Collapsible
                                excerpt={
                                    <ExcerptContainer>
                                        <div>
                                            <ListItemDetails>
                                                {[
                                                    formatDate(event.date, locale),
                                                    ...(event.endDate
                                                        ? [
                                                              locale === "fi" ? "klo" : "at",
                                                              formatTimeHour(event.date, locale),
                                                              "-",
                                                              formatTimeHour(event.endDate, locale),
                                                          ]
                                                        : []),
                                                    event.location,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                                {isAddableToCalendar(event) && (
                                                    <>
                                                        &nbsp;(
                                                        <AddToCalendarLink event={event} />)
                                                    </>
                                                )}
                                            </ListItemDetails>
                                            <ListItemDescription>
                                                {event.description[locale] ?? ""}
                                            </ListItemDescription>
                                            <ShowMoreOrLessLink
                                                href="#"
                                                style={{
                                                    height: "0px",
                                                    visibility:
                                                        !isNonExpandable(event, locale) &&
                                                        collapsed[idx]
                                                            ? "visible"
                                                            : "collapse",
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCollapsed(
                                                        collapsed.map((value, index) =>
                                                            index === idx ? !value : value
                                                        )
                                                    );
                                                }}
                                            >
                                                {translate("common.show.more")}
                                            </ShowMoreOrLessLink>
                                        </div>
                                        {isJoinable(event) && event.weblink && (
                                            <Button
                                                onClick={() =>
                                                    window.open(event.weblink || "", "_blank")
                                                }
                                            >
                                                {translate(
                                                    "customPage.eventsAndWebinars.participate"
                                                )}
                                            </Button>
                                        )}
                                    </ExcerptContainer>
                                }
                                collapsed={collapsed[idx]}
                            >
                                <ExpandedEventBlock>
                                    <p>{event.details?.[locale] ?? event.details?.fi ?? ""}</p>
                                    <ShowMoreOrLessLink
                                        href="#"
                                        style={{
                                            visibility: collapsed[idx] ? "collapse" : "visible",
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCollapsed(
                                                collapsed.map((value, index) =>
                                                    index === idx ? !value : value
                                                )
                                            );
                                        }}
                                    >
                                        {translate("common.show.less")}
                                    </ShowMoreOrLessLink>
                                </ExpandedEventBlock>
                            </Collapsible>
                        </EventContent>
                    </ListItem>
                ))}

                <Button onClick={handleButtonClick}>{showAllLabel}</Button>
            </Container>
        </>
    );
};

export default UpcomingEventsList;
